import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "sonner";
import { useLocation } from "@tanstack/react-router";
import { AuthProvider } from "@/hooks/use-auth";
import { Analytics } from "@/components/layout/Analytics";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A página que você está procurando não existe ou foi movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Voltar para o início
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-2xl w-full text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Ops! Algo deu errado
        </h1>
        <p className="mt-2 text-base text-muted-foreground">
          Não conseguimos carregar esta página. Tente recarregar ou volte para o início.
        </p>

        <div className="mt-8 p-6 bg-muted rounded-2xl text-left overflow-hidden border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              Detalhes do erro
            </h3>
            <button 
              onClick={() => {
                const details = document.getElementById('error-details');
                if (details) details.classList.toggle('hidden');
              }}
              className="text-xs font-medium text-primary hover:underline"
            >
              Alternar exibição
            </button>
          </div>
          <div id="error-details" className="space-y-4">
            <div className="bg-background p-3 rounded-lg border font-mono text-xs text-red-500 break-words">
              {error.message || "Erro desconhecido"}
            </div>
            {error.stack && (
              <pre className="bg-background p-3 rounded-lg border font-mono text-[10px] overflow-auto max-h-[300px] text-muted-foreground leading-relaxed">
                {error.stack}
              </pre>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Tentar novamente
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-xl border border-input bg-background px-6 py-3 text-sm font-bold text-foreground transition-all hover:bg-accent hover:scale-[1.02] active:scale-[0.98]"
          >
            Voltar para o início
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => {
    const canonical = typeof window !== 'undefined' ? 'https://listafornecedores.com.br' + window.location.pathname : '';
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { property: "og:site_name", content: "Gopage Fornecedores" },
      ],
      links: [
        { rel: "canonical", href: canonical },
        {
          rel: "preconnect",
          href: "https://zbxkamwbtvymtfctflwl.supabase.co",
        },
        { rel: "preload", as: "image", href: "/logo.png" },
        {
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: "/fonts/poppins-v20-latin-600.woff2",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: appCss,
        },
        { rel: "icon", href: "/favicon.png", type: "image/png" },
        { 
          rel: "preload", 
          as: "image", 
          href: "/hero.webp",
          type: "image/webp" 
        },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      ],
    }
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased text-foreground">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <HelmetProvider>
          <Helmet>
            <html lang="pt-BR" />
          </Helmet>
          <Analytics />
          <Outlet />
          <Toaster position="bottom-right" richColors />
        </HelmetProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
